import * as React from "react";

export type ToggleSize = "sm" | "md";

export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** `md` (default): h-6 w-11 + h-5 w-5 knob. `sm`: h-4 w-7 + h-3 w-3 knob. */
  size?: ToggleSize;
  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  className?: string;
}

const BASE =
  "relative inline-flex shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

const SIZE_CLASSES: Record<ToggleSize, { track: string; knob: string; on: string; off: string }> = {
  md: {
    track: "h-6 w-11",
    knob: "h-5 w-5",
    on: "translate-x-5",
    off: "translate-x-0.5",
  },
  sm: {
    track: "h-4 w-7",
    knob: "h-3 w-3",
    on: "translate-x-3.5",
    off: "translate-x-0.5",
  },
};

export const Toggle = React.forwardRef<HTMLButtonElement, ToggleProps>(
  function Toggle(
    {
      checked,
      onChange,
      disabled,
      size = "md",
      id,
      className,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      "aria-describedby": ariaDescribedBy,
    },
    ref,
  ) {
    const sz = SIZE_CLASSES[size];
    const toneClass = checked ? "bg-primary" : "bg-muted";
    const knobOffset = checked ? sz.on : sz.off;
    const classes = [BASE, sz.track, toneClass, className].filter(Boolean).join(" ");
    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        id={id}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={classes}
      >
        <span
          aria-hidden="true"
          className={`inline-block ${sz.knob} transform rounded-full bg-white shadow transition-transform ${knobOffset}`}
        />
      </button>
    );
  },
);
